import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "assets/css/styles.css";
import moment from "moment";
import { FiCheck, FiCopy } from "react-icons/fi";
import { getOutletDetails } from "actions/dashboardActions";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  confirmNcellRedemption,
  validateNcellCode,
} from "services/ncellService";
import {
  buildNcellTransactionRecord,
  findNcellTransaction,
  getHistoryCopyText,
  getNcellTransactions,
  upsertNcellTransaction,
} from "utils/ncellTransactionStorage";

const getOutletName = (outlet) => outlet?.name?.trim() || "";

const buildRedeemRemarks = (outletName, discountAmount) => {
  const amount = String(discountAmount).trim();
  return `${outletName}-${amount}`;
};

const isValidDiscountAmount = (value) => {
  const trimmed = String(value).trim();
  if (!trimmed) return false;
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount > 0;
};

const normalizeNepalPhone = (msisdn) => {
  if (!msisdn) return null;

  let digits = String(msisdn).replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("977")) {
    digits = digits.slice(3);
  }
  if (digits.length === 10) {
    return digits;
  }

  return null;
};

const formatPhoneNumber = (msisdn) => {
  const digits = normalizeNepalPhone(msisdn);
  return digits || "N/A";
};

const formatCodeOfferCode = (code, offer) => {
  const offerCode = offer?.offer_code || "N/A";
  return code ? `${code} / ${offerCode}` : offerCode;
};

const resolveDisplayResult = (transaction, inputCode, eligibleOffers) => {
  const resultCode = transaction?.transactionId || inputCode.trim();
  const localRecord = findNcellTransaction(resultCode);
  const phone = formatPhoneNumber(transaction?.msisdn);
  const effectivePhone = phone !== "N/A" ? phone : localRecord?.phone || "";
  const effectiveOffers =
    eligibleOffers.length > 0
      ? eligibleOffers
      : localRecord?.offerCode
        ? [{ offer_code: localRecord.offerCode }]
        : [];
  const primaryOffer = effectiveOffers[0];
  const resultCodeDisplay = primaryOffer
    ? formatCodeOfferCode(resultCode, primaryOffer)
    : localRecord?.offerCode
      ? `${resultCode} / ${localRecord.offerCode}`
      : resultCode;
  const copySummary = [effectivePhone, resultCodeDisplay].filter(Boolean).join("\n");

  return {
    resultCode,
    primaryOffer,
    effectiveOffers,
    copySummary,
    localRecord,
  };
};

const extractTextFromNode = (node) => {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node.text) return node.text;
  if (!Array.isArray(node.children)) return "";

  return node.children.map(extractTextFromNode).filter(Boolean).join("");
};

const getOfferConditionItems = (offer) => {
  if (!Array.isArray(offer?.offer_conditions)) return [];

  const items = [];

  offer.offer_conditions.forEach((condition) => {
    if (!Array.isArray(condition?.children)) return;

    condition.children.forEach((child) => {
      if (child?.type !== "list-item") return;

      const text = extractTextFromNode(child).trim();
      if (text) items.push(text);
    });
  });

  return items;
};

const OfferConditionsList = ({ offer }) => {
  const items = getOfferConditionItems(offer);
  if (items.length === 0) return null;

  const isOrdered = offer.offer_conditions?.some(
    (condition) => condition.format === "ordered"
  );
  const ListTag = isOrdered ? "ol" : "ul";

  return (
    <ListTag className="ncell-offer-conditions pl-3 mb-0 mt-2">
      {items.map((item, index) => (
        <li key={`${offer.offer_code}-condition-${index}`} className="text-muted">
          {item}
        </li>
      ))}
    </ListTag>
  );
};

const copyToClipboard = async (text) => {
  if (!text || text === "N/A") {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
};

const CopyResultLabel = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const canCopy = Boolean(text);

  const handleCopy = async () => {
    if (!canCopy) return;

    try {
      const success = await copyToClipboard(text);
      if (!success) return;

      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Ignore clipboard errors silently.
    }
  };

  return (
    <div className="d-flex align-items-center">
      <button
        type="button"
        className={`ncell-result-title-btn${copied ? " is-copied" : ""}`}
        onClick={handleCopy}
        disabled={!canCopy}
        title={copied ? "Copied" : "Click to copy result"}
      >
        {copied ? "Copied!" : "Copy the result"}
      </button>
      {canCopy && (
        <Button
          type="button"
          color="link"
          className="ncell-copy-btn p-0 ml-2"
          onClick={handleCopy}
          title={copied ? "Copied" : "Copy phone number and code"}
          aria-label="Copy phone number and code"
        >
          {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
        </Button>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  if (status === "PENDING") return "text-success";
  if (status === "REDEEMED") return "text-info";
  if (status === "INELIGIBLE") return "text-warning";
  return "text-muted";
};

const getEligibleOffers = (transaction) =>
  Array.isArray(transaction?.data) ? transaction.data : [];

const isRedeemable = (transaction) => {
  const offers = getEligibleOffers(transaction);
  if (!transaction?.success || offers.length === 0) return false;
  if (transaction.status === "REDEEMED" || transaction.status === "INELIGIBLE") {
    return false;
  }
  return transaction.eligible === true || transaction.status === "PENDING";
};

const isSuccessfulTransaction = (data) => {
  if (!data?.success) return false;
  if (data.status === "INELIGIBLE") return false;

  const offers = getEligibleOffers(data);
  return (
    data.status === "REDEEMED" ||
    (data.eligible === true && offers.length > 0) ||
    data.status === "PENDING"
  );
};

const NcellView = () => {
  const dispatch = useDispatch();
  const { outlet } = useSelector((state) => state.outletDetail);
  const outletName = getOutletName(outlet);
  const [transactionCode, setTransactionCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [checking, setChecking] = useState(false);
  const [redeemingOfferCode, setRedeemingOfferCode] = useState("");
  const [heldCopySummary, setHeldCopySummary] = useState("");
  const [heldOffers, setHeldOffers] = useState([]);
  const [activeTab, setActiveTab] = useState("check");
  const [savedTransactions, setSavedTransactions] = useState([]);

  const refreshSavedTransactions = () => {
    setSavedTransactions(getNcellTransactions());
  };

  useEffect(() => {
    refreshSavedTransactions();
    if (!outlet) {
      dispatch(getOutletDetails());
    }
  }, [dispatch, outlet]);

  const persistSuccessfulTransaction = (data, code, status, offers) => {
    const offerList = offers || getEligibleOffers(data);
    const record = buildNcellTransactionRecord({
      transaction: { ...data, data: offerList },
      code,
      status,
      offers: offerList,
    });

    if (!record.code) return;

    upsertNcellTransaction(record);
    refreshSavedTransactions();
  };

  const markTransactionRedeemed = (code, fallbackOffers = []) => {
    const normalizedCode = code.trim();
    if (!normalizedCode) return;

    const existing = findNcellTransaction(normalizedCode);
    if (existing) {
      upsertNcellTransaction(
        { code: normalizedCode, status: "REDEEMED" },
        { redeemOnly: true }
      );
    } else {
      const record = buildNcellTransactionRecord({
        transaction,
        code: normalizedCode,
        status: "REDEEMED",
        offers: fallbackOffers,
      });
      upsertNcellTransaction(record);
    }

    refreshSavedTransactions();
  };

  const eligibleOffers = getEligibleOffers(transaction);
  const canRedeem = isRedeemable(transaction);
  const resolvedResult = transaction
    ? resolveDisplayResult(transaction, transactionCode, eligibleOffers)
    : null;
  const resultCode = resolvedResult?.resultCode || transactionCode.trim();
  const primaryOffer = resolvedResult?.primaryOffer;
  const copySummary = resolvedResult?.copySummary || "";
  const displayCopySummary = copySummary || heldCopySummary;
  const displayOffers = canRedeem
    ? eligibleOffers
    : transaction?.status === "REDEEMED"
      ? resolvedResult?.effectiveOffers?.length
        ? resolvedResult.effectiveOffers
        : heldOffers
      : [];

  const handleCheck = async (event) => {
    event.preventDefault();

    const code = transactionCode.trim();
    if (!code) {
      setError("Please enter a code.");
      return;
    }

    setChecking(true);
    setError("");
    setSuccessMessage("");
    setHeldCopySummary("");
    setHeldOffers([]);
    setTransaction(null);

    try {
      const data = await validateNcellCode(code);
      setTransaction(data);

      if (isSuccessfulTransaction(data)) {
        persistSuccessfulTransaction(data, code, data.status);
      }

      const saved = findNcellTransaction(data.transactionId || code);
      if (saved?.offerCode && getEligibleOffers(data).length === 0) {
        setHeldOffers([{ offer_code: saved.offerCode }]);
      }
      if (saved) {
        setHeldCopySummary(getHistoryCopyText(saved));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setChecking(false);
    }
  };

  const handleRedeem = async (offer) => {
    const callbackUrl = offer.benefit?.callbacks?.[0] || "";

    if (!callbackUrl) {
      setError("Redeem callback URL is missing for this offer.");
      return;
    }

    if (!outletName) {
      setError("Outlet name is missing. Cannot redeem without remarks.");
      return;
    }

    if (!isValidDiscountAmount(discountAmount)) {
      setError("Discount amount is required to redeem.");
      return;
    }

    const redeemRemarks = buildRedeemRemarks(outletName, discountAmount);

    setRedeemingOfferCode(offer.offer_code);
    setError("");
    setSuccessMessage("");

    try {
      if (copySummary) {
        setHeldCopySummary(copySummary);
        setHeldOffers(eligibleOffers);
      }

      await confirmNcellRedemption({
        callbackUrl,
        remarks: redeemRemarks,
      });
      setSuccessMessage("Ncell offer redeemed successfully.");

      markTransactionRedeemed(
        transactionCode,
        heldOffers.length ? heldOffers : eligibleOffers
      );

      await handleRefreshAfterRedeem();
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeemingOfferCode("");
    }
  };

  const handleRefreshAfterRedeem = async () => {
    const code = transactionCode.trim();
    if (!code) return;

    const data = await validateNcellCode(code);
    setTransaction(data);

    if (isSuccessfulTransaction(data)) {
      persistSuccessfulTransaction(data, code, data.status);
    }

    const saved = findNcellTransaction(data.transactionId || code);
    if (saved?.offerCode) {
      setHeldOffers([{ offer_code: saved.offerCode }]);
    }
    if (saved) {
      setHeldCopySummary(getHistoryCopyText(saved));
    }
  };

  const hasValidDiscountAmount = isValidDiscountAmount(discountAmount);

  return (
    <div className="content">
      <Row>
        <Col md="8">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Ncell Redeem</CardTitle>
              <p className="category">
                Check a code and redeem an eligible Ncell offer.
              </p>
            </CardHeader>
            <CardBody>
              <Nav tabs className="ncell-tabs mb-4">
                <NavItem>
                  <NavLink
                    className={activeTab === "check" ? "active" : ""}
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveTab("check");
                    }}
                  >
                    Check Code
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === "history" ? "active" : ""}
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      refreshSavedTransactions();
                      setActiveTab("history");
                    }}
                  >
                    Today ({savedTransactions.length})
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="check">
              <Form onSubmit={handleCheck}>
                <Row className="align-items-end">
                  <Col xs="12" sm="5" md="4" lg="3">
                    <FormGroup className="ncell-code-field mb-3">
                      <Label for="ncell-transaction-code" className="text-dark font-weight-bold">
                        Code
                      </Label>
                      <Input
                        id="ncell-transaction-code"
                        className="form_input ncell-code-input"
                        placeholder="Example: 9587Z0"
                        value={transactionCode}
                        onChange={(event) =>
                          setTransactionCode(event.target.value.toUpperCase())
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col
                    xs="12"
                    sm="7"
                    md="8"
                    lg="9"
                    className="d-flex align-items-end mb-3 ncell-action-buttons"
                  >
                    <Button
                      className="button-light-gray"
                      disabled={checking}
                      type="submit"
                    >
                      {checking ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Checking
                        </>
                      ) : (
                        "Check Code"
                      )}
                    </Button>
                  </Col>
                </Row>

                <Row className="align-items-end">
                  <Col xs="12" sm="5" md="4" lg="3">
                    <FormGroup className="mb-3">
                      <Label for="ncell-discount-amount" className="text-dark font-weight-bold">
                        Discount Amount <span className="text-dark">*</span>
                      </Label>
                      <Input
                        id="ncell-discount-amount"
                        className="form_input"
                        type="number"
                        min="1"
                        step="1"
                        placeholder="e.g. 300"
                        value={discountAmount}
                        onChange={(event) => setDiscountAmount(event.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col
                    xs="12"
                    sm="7"
                    md="8"
                    lg="9"
                    className="d-flex align-items-end mb-3 ncell-action-buttons"
                  >
                    <Button
                      className="button-green"
                      type="button"
                      disabled={
                        checking ||
                        redeemingOfferCode ||
                        !canRedeem ||
                        !primaryOffer?.benefit?.callbacks?.[0] ||
                        !outletName ||
                        !hasValidDiscountAmount
                      }
                      onClick={() => primaryOffer && handleRedeem(primaryOffer)}
                    >
                      {redeemingOfferCode ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Redeeming
                        </>
                      ) : (
                        "Redeem"
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>

              {error && (
                <div className="alert alert-danger mt-2" role="alert">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success mt-2" role="alert">
                  {successMessage}
                </div>
              )}

              {transaction && (
                <div className="ncell-result mt-4">
                  <CopyResultLabel text={displayCopySummary} />
                  {displayCopySummary && (
                    <pre className="ncell-result-output mb-0">
                      {displayCopySummary}
                    </pre>
                  )}

                  <Row className="mt-3">
                    <Col md="6">
                      <p className="mb-2">
                        <strong>Status:</strong>{" "}
                        <span className={getStatusColor(transaction.status)}>
                          {transaction.status || "VALID"}
                        </span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="mb-2">
                        <strong>Partner:</strong> {transaction.partner || "N/A"}
                      </p>
                    </Col>
                  </Row>

                  {canRedeem ? (
                    <>
                      {eligibleOffers.map((offer) => (
                        <Card className="mb-3" key={offer.offer_code}>
                          <CardBody>
                            <p className="text-dark mb-1 font-weight-bold">
                              {offer.offer_code}
                            </p>
                            <OfferConditionsList offer={offer} />
                          </CardBody>
                        </Card>
                      ))}
                    </>
                  ) : transaction.status === "REDEEMED" ? (
                    <>
                      <div className="alert alert-info mt-3" role="alert">
                        This code has already been redeemed. You can still copy
                        the result above.
                      </div>
                      {displayOffers.map((offer) => (
                        <Card className="mb-3" key={offer.offer_code}>
                          <CardBody>
                            <p className="text-dark mb-1 font-weight-bold">
                              {offer.offer_code}
                            </p>
                            <OfferConditionsList offer={offer} />
                          </CardBody>
                        </Card>
                      ))}
                    </>
                  ) : transaction.status === "INELIGIBLE" ? (
                    <div className="alert alert-warning mt-3" role="alert">
                      This code is not eligible for redemption.
                    </div>
                  ) : (
                    <div className="alert alert-warning mt-3" role="alert">
                      This code is not currently redeemable.
                    </div>
                  )}
                </div>
              )}
                </TabPane>

                <TabPane tabId="history">
                  {savedTransactions.length === 0 ? (
                    <p className="text-muted mb-0">
                      No successful transactions saved today. Checked codes are
                      kept locally for 24 hours.
                    </p>
                  ) : (
                    savedTransactions.map((item) => {
                      const historyCopyText = getHistoryCopyText(item);

                      return (
                      <Card className="mb-3" key={item.code}>
                        <CardBody>
                          <div className="d-flex align-items-start justify-content-between flex-wrap">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center flex-wrap mb-2">
                                <span
                                  className={`badge badge-${item.status === "REDEEMED" ? "info" : "success"} mr-2`}
                                >
                                  {item.status || "VALID"}
                                </span>
                                <small className="text-muted">
                                  {moment(item.savedAt).format("h:mm A")}
                                </small>
                              </div>
                              <pre className="ncell-result-output mb-2">
                                {historyCopyText}
                              </pre>
                              <p className="text-muted mb-0 small">
                                {item.phone && (
                                  <>
                                    <strong>Phone:</strong> {item.phone}
                                    {(item.code || item.offerCode) && " · "}
                                  </>
                                )}
                                {item.code && (
                                  <>
                                    <strong>Code:</strong> {item.code}
                                    {item.offerCode && " · "}
                                  </>
                                )}
                                {item.offerCode && (
                                  <>
                                    <strong>Offer Code:</strong> {item.offerCode}
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="d-flex align-items-center ml-md-3 mt-2 mt-md-0">
                              <Button
                                type="button"
                                color="link"
                                className="ncell-copy-btn p-0 mr-2"
                                title="Copy result"
                                onClick={() => copyToClipboard(historyCopyText)}
                              >
                                <FiCopy size={16} />
                              </Button>
                              <Button
                                className="button-light-gray"
                                onClick={() => {
                                  setTransactionCode(item.code);
                                  setActiveTab("check");
                                }}
                              >
                                Open
                              </Button>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    );
                    })
                  )}
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NcellView;
